import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestfulApiService } from '../restful-api.service';
import {
  ChampionTestimonialRequest,
  ChampionTestimonialResponse,
  TestimonialStatus,
  ResubmitChampionRequest
} from '../../models/champion-testimonial.model';

@Injectable({
  providedIn: 'root'
})
export class ChampionTestimonialService {
  private readonly baseEndpoint = '/v1/admin/champion-testimonials';

  constructor(private restfulApiService: RestfulApiService) {}

  /**
   * Fetches testimonials with filtering, pagination, and search
   * @param request The request parameters including status filters, pagination, and search text
   * @returns Observable of the testimonial response
   */
  getTestimonials(request: ChampionTestimonialRequest): Observable<ChampionTestimonialResponse> {
    return this.restfulApiService.post(
      this.baseEndpoint,
      request
    ) as Observable<ChampionTestimonialResponse>;
  }

  /**
   * Gets testimonials with default filters
   * @param page Page number (default: 0)
   * @param size Page size (default: 18)
   * @param searchText Optional search text
   * @returns Observable of the testimonial response
   */
  getTestimonialsWithDefaults(
    page = 0,
    size = 18,
    searchText = ''
  ): Observable<ChampionTestimonialResponse> {
    const request: ChampionTestimonialRequest = {
      statusList: ['LIVE', 'DRAFT', 'PENDING', 'ARCHIVED', 'DECLINED', 'EXPIRED'],
      size,
      page,
      searchText
    };
    return this.getTestimonials(request);
  }

  /**
   * Gets testimonials filtered by specific statuses
   * @param statuses Array of status filters
   * @param page Page number
   * @param size Page size
   * @param searchText Optional search text
   * @returns Observable of the testimonial response
   */
  getTestimonialsByStatus(
    statuses: TestimonialStatus[],
    page = 0,
    size = 18,
    searchText = ''
  ): Observable<ChampionTestimonialResponse> {
    const request: ChampionTestimonialRequest = {
      statusList: statuses,
      size,
      page,
      searchText
    };
    return this.getTestimonials(request);
  }

  /**
   * Gets champion testimonials filtered by specific statuses via the champion endpoint
   * @param statuses Array of status filters
   * @param page Page number
   * @param size Page size
   * @param searchText Optional search text
   * @returns Observable of the testimonial response
   */
  getChampionTestimonialsByStatus(
    statuses: TestimonialStatus[],
    page = 0,
    size = 18,
    searchText = ''
  ): Observable<ChampionTestimonialResponse> {
    const request: ChampionTestimonialRequest = {
      statusList: statuses,
      size,
      page,
      searchText
    };
    return this.restfulApiService.post(
      '/v1/champion/testimonials',
      request
    ) as Observable<ChampionTestimonialResponse>;
  }

  /**
   * Archives a testimonial by its UUID
   * @param uuid The UUID of the testimonial to archive
   * @returns Observable of the API response
   */
  archiveTestimonial(uuid: string): Observable<any> {
    return this.restfulApiService.post(
      `/v1/champion/testimonial-archive/${uuid}`,
      {}
    );
  }

  /**
   * Deletes a testimonial by its UUID
   * @param uuid The UUID of the testimonial to delete
   * @returns Observable of the API response
   */
  deleteTestimonial(uuid: string): Observable<any> {
    return this.restfulApiService.delete(
      `/v1/champion/testimonial-delete/${uuid}`,
      {}
    );
  }

  /**
   * Approves a testimonial (admin) by its UUID
   * @param uuid The UUID of the testimonial to approve
   * @returns Observable of the API response
   */
  approveTestimonial(uuid: string): Observable<any> {
    return this.restfulApiService.post(
      '/v1/admin/champion-testimonial-approve',
      { uuid }
    );
  }

  /**
   * Declines a testimonial (admin) by its UUID with a reason
   * @param uuid The UUID of the testimonial to decline
   * @param reason The reason for declining
   * @returns Observable of the API response
   */
  declineTestimonial(uuid: string, reason: string): Observable<any> {
    return this.restfulApiService.post(
      '/v1/admin/champion-testimonial-decline',
      { uuid, string1: reason }
    );
  }

  /**
   * Archives a testimonial (admin) by its UUID
   * @param uuid The UUID of the testimonial to archive
   * @returns Observable of the API response
   */
  archiveAdminTestimonial(uuid: string): Observable<any> {
    return this.restfulApiService.post(
      `/v1/admin/champion-testimonial-archive/${uuid}`,
      {}
    );
  }

  /**
   * Deletes a testimonial (admin) by its UUID
   * @param uuid The UUID of the testimonial to delete
   * @returns Observable of the API response
   */
  deleteAdminTestimonial(uuid: string): Observable<any> {
    return this.restfulApiService.delete(
      `/v1/admin/champion-testimonial-delete/${uuid}`,
      {}
    );
  }

  /**
   * Restores an archived testimonial (admin) by its UUID
   * @param uuid The UUID of the testimonial to restore
   * @returns Observable of the API response
   */
  restoreTestimonial(uuid: string): Observable<any> {
    return this.restfulApiService.post(
      `/v1/admin/champion-testimonial-restore/${uuid}`,
      {}
    );
  }

  /**
   * Resubmits a champion testimonial to link with an organization
   * @param request The resubmit request containing organization UUID, link UUID, and champion data
   * @returns Observable of the API response
   */
  resubmitChampion(request: ResubmitChampionRequest): Observable<any> {
    return this.restfulApiService.post(
      '/v1/champion/link-organisation-submit',
      request
    );
  }
}
